/**
 * Преобразует дату из ISO-формата (сервер) в формат для datetime-local
 * @param {string} date - Дата в формате ISO (например, "2025-08-02T14:43:00.000Z")
 * @returns {string|null} Дата в формате "YYYY-MM-DDTHH:MM" или null, если дата невалидна
 */
export function formatDateTimeLocal(date) {
  if (!date) return null;
  const d = new Date(date);
  if (isNaN(d.getTime())) return null;

  const pad = (num) => num.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
    d.getDate()
  )}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function formatDateAndTimeLocal(date) {
  if (!date) return "—";
  return new Date(date).toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatDateOnly(date) {
  if (!date) return null;
  const d = new Date(date);
  if (isNaN(d.getTime())) return null;

  const pad = (num) => num.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
    d.getDate()
  )}`;
}

/**
 * Преобразует дату из формата datetime-local обратно в ISO-формат
 * @param {string} datetime - Дата в формате "YYYY-MM-DDTHH:MM"
 * @returns {string|null} Дата в формате ISO (например, "2025-08-02T14:43:00.000Z")
 */
export function parseDateTimeLocal(datetime) {
  if (!datetime) return null;
  return new Date(datetime + ":00.000Z").toISOString();
}

export function parseDateOnly(date) {
  if (!date) return null;
  return new Date(date).toISOString();
}

/**
 * Форматирует дату и время в формат "DD.MM.YYYY, HH:mm"
 */
export function formatDateAndTime(date) {
  if (!date) return "—";
  const parts = parseDisplayDateParts(date);
  if (!parts) return "—";

  return `${parts.day}.${parts.month}.${parts.year}, ${parts.hour}:${parts.minute}`;
}

/**
 * Форматирует дату в формат "DD.MM.YYYY"
 */
export function formatDate(date) {
  if (!date) return "—";
  const parts = parseDisplayDateParts(date);
  if (!parts) return "—";

  return `${parts.day}.${parts.month}.${parts.year}`;
}

// Saby в XML ЭДО может вернуть дату как `28.08.2026T13:30:00+07:00`,
// а наш API чаще отдаёт ISO `2026-08-28T13:30:00+07:00`. Для read-only
// карточек важно поддерживать оба формата без преобразования через Date:
// иначе браузер может сдвинуть время по локальной таймзоне пользователя.
function parseDisplayDateParts(date) {
  const str = String(date);
  const isoMatch = str.match(/(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/);
  if (isoMatch) {
    return {
      year: isoMatch[1],
      month: isoMatch[2],
      day: isoMatch[3],
      hour: isoMatch[4] || "00",
      minute: isoMatch[5] || "00",
    };
  }

  const sabyMatch = str.match(/(\d{2})\.(\d{2})\.(\d{4})(?:T(\d{2}):(\d{2}))?/);
  if (sabyMatch) {
    return {
      year: sabyMatch[3],
      month: sabyMatch[2],
      day: sabyMatch[1],
      hour: sabyMatch[4] || "00",
      minute: sabyMatch[5] || "00",
    };
  }

  return null;
}

/**
 * Возвращает текущую дату в формате "YYYY-MM-DD"
 */
export function currentDateOnly() {
  const d = new Date();
  const pad = (num) => num.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
    d.getDate()
  )}`;
}

/**
 * Возвращает текущую дату и время в формате "YYYY-MM-DDTHH:MM" для datetime-local
 */
export function currentDatetime() {
  return formatDateTimeLocal(new Date());
}

/**
 * Возвращает ключ группировки для журнала событий в формате "YYYY-MM-DD"
 * @param {string} createdAt - Дата в ISO-формате
 * @returns {string} Дата в формате "YYYY-MM-DD" или "" при невалидной дате
 */
export function formatActivityLogDateKey(createdAt) {
  return formatDateOnly(createdAt) ?? "";
}

/**
 * Возвращает читаемый заголовок дня для журнала событий на русском языке.
 * Если дата совпадает с today — добавляет префикс "Сегодня".
 * @param {string} createdAt - Дата в ISO-формате
 * @param {string} today - Текущая дата в формате "YYYY-MM-DD" (из currentDateOnly())
 * @returns {string} Например: "Сегодня, воскресенье, 15 июня" или "Воскресенье, 15 июня 2024"
 */
export function formatActivityLogDateLabel(createdAt, today) {
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return "";

  const dateKey = formatActivityLogDateKey(createdAt);
  const weekday = new Intl.DateTimeFormat("ru-RU", {
    weekday: "long",
  }).format(date);
  const formattedDate = new Intl.DateTimeFormat("ru-RU", {
    day: "numeric",
    month: "long",
    ...(date.getFullYear() !== new Date().getFullYear()
      ? { year: "numeric" }
      : {}),
  }).format(date);
  const capitalizedWeekday =
    weekday.charAt(0).toUpperCase() + weekday.slice(1);

  return dateKey === today
    ? `Сегодня, ${weekday}, ${formattedDate}`
    : `${capitalizedWeekday}, ${formattedDate}`;
}

/**
 * Возвращает метку часа для журнала событий в формате "HH:00"
 * @param {string} createdAt - Дата в ISO-формате
 * @returns {string} Например: "09:00" или "" при невалидной дате
 */
export function formatActivityLogHourLabel(createdAt) {
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return "";
  return `${String(date.getHours()).padStart(2, "0")}:00`;
}

/**
 * Возвращает дату и время отдельными строками — для подписи под графиком (в столбик).
 * @param {string} date - Дата в ISO-формате
 * @returns {{date: string, time: string}} Например: { date: "02.07", time: "11:26" }
 */
export function formatChartDateTimeParts(date) {
  if (!date) return { date: "", time: "" };
  const d = new Date(date);
  if (isNaN(d.getTime())) return { date: "", time: "" };
  const pad = (n) => String(n).padStart(2, "0");
  return {
    date: `${pad(d.getDate())}.${pad(d.getMonth() + 1)}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
}

// Показывает ISO/XSD-время в заданном поясе точки, сохраняя момент события.
// Без timezone сохраняет исходные часы для существующих ЭДО-форм.
import { DateTime } from "luxon";
export function formatEdoDateTimeLocal(value, timezone = "") {
  if (!value) return "";
  const str = String(value).trim();
  const iso = str.replace(/^(\d{2})\.(\d{2})\.(\d{4})T/, "$3-$2-$1T");
  const zoned = DateTime.fromISO(iso, { setZone: true, zone: timezone || "UTC" });
  if (zoned.isValid) {
    const local = timezone ? zoned.setZone(timezone) : zoned;
    return local.isValid ? local.toFormat("yyyy-MM-dd'T'HH:mm") : "";
  }
  return timezone ? "" : iso.slice(0, 16);
}
